import { ApiCampaignType, ApiSimpleCampaign } from "@incendium/api";
import { Button, Stack } from "@mui/material";
import Loading from "Components/Loading/Loading";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { useMount, useSelectedProject } from "Hooks";
import { useCampaigns } from "Hooks/useCampaigns";
import { CampaignList, CampaignSetup } from "features/campaigns";
import { useCallback, useEffect, useState } from "react";

const SETUP_PARAM = "setup";

function parseSetupId(value: string | null): number | null {
  if (value === null || value === "") return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
}

function ConversionLabPage() {
  const { selectedProject } = useSelectedProject();
  const [open, setOpen] = useState(false);
  const [campaign, setCampaign] = useState<ApiSimpleCampaign>({});
  const { campaigns, loading, refetch } = useCampaigns();
  const [setupId, setSetupId] = useAppendSearchState<number | null>(SETUP_PARAM, null, {
    serialize: (v) => (v === null ? null : String(v)),
    deserialize: (s) => parseSetupId(s),
  });

  useMount(() => {
    if (campaigns.length > 0) {
      refetch();
    }
  });

  useEffect(() => {
    if (setupId === null || campaigns.length === 0) return;
    const found = campaigns.find((c) => c.id === setupId);
    if (found) {
      setCampaign(found);
      setOpen(true);
    }
  }, [setupId, campaigns]);

  const newCampaign = useCallback((type: ApiCampaignType) => {
    setCampaign({ type });
    setOpen(true);
  }, []);

  const handleSetOpen = useCallback((value: React.SetStateAction<boolean>) => {
    setOpen(value);
    const next = typeof value === "function" ? value(open) : value;
    if (!next) {
      setSetupId(null);
    }
  }, [setSetupId, open]);

  if (loading) {
    return <Loading text="Fetching A/B Testing Campaigns" />;
  }

  return (
    <>
      <CampaignList
        project={selectedProject!}
        setOpen={setOpen}
        setCampaign={setCampaign}
        onEditCampaign={(c) => setSetupId(c.id ?? null)}
        types={[
          ApiCampaignType.CAMPAIGN_TYPE_AB,
          ApiCampaignType.CAMPAIGN_TYPE_SPLIT,
        ]}
        actions={
          <Stack direction={"row"} spacing={2}>
            <Button
              onClick={() => newCampaign(ApiCampaignType.CAMPAIGN_TYPE_AB)}
            >
              New A/B Campaign
            </Button>
            <Button
              onClick={() => newCampaign(ApiCampaignType.CAMPAIGN_TYPE_SPLIT)}
            >
              New Split Campaign
            </Button>
          </Stack>
        }
      />

      <CampaignSetup
        open={open}
        setOpen={handleSetOpen}
        campaign={campaign}
        setCampaign={setCampaign}
      />
    </>
  );
}

export default ConversionLabPage;
